import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label?: ReactNode;
  hint?: ReactNode;
  /** Dark-mode switch uses --switch-off; other settings toggles keep --toggle-off. */
  track?: 'switch' | 'toggle';
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Toggle(
  { checked, onCheckedChange, label, hint, disabled, className, track = 'toggle', ...props },
  ref,
) {
  return (
    <div className={cn('flex items-start gap-s3', disabled && 'text-ds-muted', className)}>
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'relative h-6 w-10 shrink-0 rounded-full p-0.5 transition-colors',
          checked ? 'bg-ds-teal' : track === 'switch' ? 'bg-ds-switch-off' : 'bg-ds-toggle-off',
          disabled && 'cursor-not-allowed opacity-70',
        )}
        {...props}
      >
        <span
          className={cn(
            'block h-5 w-5 rounded-full bg-ds-knob shadow-sm transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </button>
      {(label || hint) && (
        <span className="min-w-0">
          {label ? <span className={cn(TYPE.body, 'block')}>{label}</span> : null}
          {hint ? <span className={cn(TYPE.small, 'block text-ds-muted')}>{hint}</span> : null}
        </span>
      )}
    </div>
  );
});
